import {CircleCheckIcon, SparklesIcon} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@tinct/web-components';
import {ReadQuota} from '@tinct/core-api-models';
import type {HashParams} from '../hooks/use-paywall-hash';

/**
 * Features of a higher plan. Offering one is not an error: the alert reads as the product's own
 * upsell (the brand's indigo `important` variant, as the Enterprise badge of the security page), not
 * in the red of a limit that was hit.
 */
const FEATURE_UPSELL_CONTENT = {
  'role': {
    title: 'Some roles requires Pro',
    description: 'Admin and Viewer roles are available on Pro or agency plan. Free plan includes Owner and Editor roles.',
  },
  'integration': {
    title: 'Integrations require Pro',
    description: 'To connect HubSpot and other services, you need a Pro Plan.',
  },
  'sso': {
    title: 'Single sign-on is an Enterprise feature',
    description: 'Let your team sign in with your identity provider (Okta, Entra ID, Google Workspace…), on your own verified domains. Talk to us and we switch it on for this workspace.',
  },
} as const;

/**
 * - `success`: good news with a limit attached (more page variants ready than the plan publishes);
 * - `upsell`: a feature of another plan, offered;
 * - `limit`: a quota the workspace ran out of, which blocks what it was doing.
 */
type AlertTone = 'success' | 'upsell' | 'limit';

type AlertContent = { title: string; description: string; tone: AlertTone };

function getAlertContent(
  reason: string | undefined,
  { isNotEnoughCredits, isNotEnoughLivePages }: Pick<PaywallAlertProps, 'isNotEnoughCredits' | 'isNotEnoughLivePages'>,
  creditQuota: ReadQuota | undefined,
  lpQuota: ReadQuota | undefined,
  totalPages: number | undefined,
): AlertContent | null {
  if (!reason) return null;

  if (isNotEnoughCredits) {
    return {
      title: `You have ${creditQuota?.remainingQuota ?? 0} credits left`,
      description: 'To continue generating pages, please upgrade your account.',
      tone: 'limit',
    };
  }

  if (isNotEnoughLivePages) {
    return {
      title: `Great news: ${totalPages ?? 0} page variants ready !`,
      description: `Your current plan allows you to publish the top ${lpQuota?.remainingQuota ?? 0} page variants.`,
      tone: 'success',
    };
  }

  if (reason in FEATURE_UPSELL_CONTENT) {
    return {
      ...FEATURE_UPSELL_CONTENT[reason as keyof typeof FEATURE_UPSELL_CONTENT],
      tone: 'upsell',
    };
  }

  return null;
}

export type PaywallAlertProps = {
  hashParams: HashParams | null;
  /** From `usePaywallHash`, which owns the classification of the reason: not re-derived here. */
  isNotEnoughCredits: boolean;
  isNotEnoughLivePages: boolean;
  creditQuota: ReadQuota | undefined;
  lpQuota: ReadQuota | undefined;
};

export function PaywallAlert({
  hashParams,
  isNotEnoughCredits,
  isNotEnoughLivePages,
  creditQuota,
  lpQuota,
}: PaywallAlertProps) {
  const alertContent = getAlertContent(
    hashParams?.reason,
    { isNotEnoughCredits, isNotEnoughLivePages },
    creditQuota,
    lpQuota,
    hashParams?.totalPages,
  );

  if (!alertContent) return null;

  if (alertContent.tone === 'success') {
    return (
      <Alert className="rounded-2xl border-emerald-100 bg-emerald-50 py-4 text-emerald-800">
        <CircleCheckIcon className="size-4" />
        <AlertTitle>{alertContent.title}</AlertTitle>
        <AlertDescription className="text-emerald-700">{alertContent.description}</AlertDescription>
      </Alert>
    );
  }

  if (alertContent.tone === 'upsell') {
    return (
      <Alert variant="important" className="py-4">
        <SparklesIcon className="size-4" />
        <AlertTitle>{alertContent.title}</AlertTitle>
        <AlertDescription>{alertContent.description}</AlertDescription>
      </Alert>
    );
  }

  return (
    <Alert variant="destructive" className="rounded-2xl border-rose-100 bg-rose-50 py-4">
      <CircleCheckIcon className="size-4" />
      <AlertTitle>{alertContent.title}</AlertTitle>
      <AlertDescription className="text-destructive/90">{alertContent.description}</AlertDescription>
    </Alert>
  );
}
